import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getStateFromLocalStorage, storeStateAtLocalStorage } from '@src/reduxjs'
import { mainApiUrl, baseHeaders } from '@constants'

const initialState = {
  album: {
    id: null,
    name: null,
  },
  category: {
    id: null,
    name: null,
  },
  labels: [],
  upload: {
    inProgress: false,
    abortRequested: false,
    isCompleted: false,
  },
  drop: {
    inProgress: false,
    isCompleted: false,
  },
  photographer: {
    id: null,
    name: null,
  },
  detectedOwner: {
    id: null,
    matchPercent: 0,
    name: '',
    storeDetected: false,
  },
  isPrivate: false,
  tags: [],
}

export const getPhotoOwnerThunk = createAsyncThunk('upload/getPhotoOwnerThunk', async (data, { getState, rejectWithValue }) => {
  const body = JSON.stringify(data)
  const headers = { ...baseHeaders, Authorization: `JWT ${getState().auth.jwtToken}` }

  const response = await fetch(`${mainApiUrl}/photos/owner`, { body, headers, method: 'POST' })
  const json = await response.json()

  if (response.status !== 200) {
    return rejectWithValue({ ...json, status: response.status })
  }

  return json
})

const uploadSlice = createSlice({
  name: 'upload',
  initialState: { ...initialState, ...getStateFromLocalStorage('uploadSlice') },
  reducers: {
    setAlbum: (state, action) => {
      state.album = action.payload || {}
      storeStateAtLocalStorage('uploadSlice', { album: state.album })
    },
    setLabels: (state, action) => {
      state.labels = action.payload || initialState.labels
    },
    toggleSelectedLabel: (state, { payload }) => {
      if (state.labels.some(({ id }) => id === payload.id)) {
        state.labels = state.labels.filter(({ id }) => id !== payload.id)

        return state
      }

      state.labels = [...state.labels, payload]
    },
    toggleSingleSelectedLabel: (state, { payload }) => {
      if (state.labels.some((i) => i.id === payload.id)) {
        state.labels = state.labels.filter((i) => i.group !== payload.group && i.id !== payload.id)
        return state
      }
      state.labels = [...state.labels.filter((i) => i.group !== payload.group), payload]
    },
    toggleIsPrivate: (state, action) => {
      state.isPrivate = [false, true].includes(action.payload) ? action.payload : !state.isPrivate
    },
    setUploadAbortRequested: (state, action) => {
      state.upload.abortRequested = action.payload
    },
    setUploadInProgress: (state, action) => {
      state.upload.inProgress = action.payload
    },
    setUploadCompleted: (state, action) => {
      state.upload.isCompleted = action.payload
    },
    setDropInProgress: (state, action) => {
      state.drop.inProgress = action.payload
    },
    setDropCompleted: (state, action) => {
      state.drop.isCompleted = action.payload
    },
    setPhotographer: (state, action) => {
      if (state.photographer.id === action.payload.id) {
        state.photographer = initialState.photographer

        return state
      }

      state.photographer = action.payload || initialState.photographer
    },
    resetUpload: (state) => {
      state.upload = initialState.upload
    },
    resetDetectedOwner: (state) => {
      state.detectedOwner = initialState.detectedOwner
    },
    setTags: (state, action) => {
      state.tags = action.payload || initialState.tags
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(({ type }) => type === `${getPhotoOwnerThunk.fulfilled}`, (state, { payload }) => {
      const { data, meta } = payload

      if (data && data.id) {
        state.detectedOwner.id = data.id
        state.detectedOwner.matchPercent = meta.match_percent
        state.detectedOwner.name = data.name
        state.detectedOwner.storeDetected = meta.store_detected
      }
    })
  },
})

export const {
  setAlbum,
  toggleSelectedLabel,
  toggleSingleSelectedLabel,
  setLabels,
  toggleIsPrivate,
  setUploadAbortRequested,
  setUploadInProgress,
  setUploadCompleted,
  resetUpload,
  resetDetectedOwner,
  setTags,
  setDropCompleted,
  setDropInProgress,
  setPhotographer,
} = uploadSlice.actions

export default uploadSlice
